import Dexie, { type Table } from 'dexie'

/** Persistimos la cola offline en IndexedDB antes de sincronizar con Supabase. */

// Guardamos las fotos como ArrayBuffer (no Blob) porque WebKit/Safari tiene un bug
// documentado donde un Blob queda en 0 bytes al releerlo de IndexedDB tiempo despues
// de guardarlo (ver docs/hallazgo-no-content-provided-china.md). ArrayBuffer no sufre
// ese problema. Reconstruimos el Blob solo al momento de mostrarlo o subirlo.

export interface TelaPendiente {
  id: string
  muestrario: string
  colores: string[]
  tipo: string | null
  fotoFrenteBlob?: ArrayBuffer
  fotoReversoBlob?: ArrayBuffer
  /**
   * URL/key real ya subida — igual criterio que `FotoProductoPendiente.url`
   * (reutilizar si el blob se vació a las 72h) MÁS un segundo propósito
   * agregado 2026-09-07: saber en qué storage (Supabase o S3) vive de
   * verdad cada foto, para poder borrarla del lugar correcto sin adivinar
   * ni intentar en los dos a ciegas — ver `backendDeUrl` en rutas-storage.ts
   * y el borrado en cascada en casos-de-uso.ts.
   */
  fotoFrenteUrl?: string
  fotoReversoUrl?: string
}

export interface ProveedorPendiente {
  id?: number
  codigo: string
  nombre: string
  ubicacion: string
  contacto: string
  telefono: string
  tarjetaBlob?: ArrayBuffer
  creadoEn: number
  sincronizado: 0 | 1
  intentos: number
  ultimoError?: string
  /**
   * `true` desde la primera vez que este proveedor se sincronizó con éxito, y
   * NUNCA vuelve a `false` después (aunque se edite y quede `sincronizado: 0`
   * de nuevo mientras se resincroniza). Es la única forma confiable de saber
   * si existe una fila remota (Postgres + Storage) que haya que borrar al
   * eliminar — `sincronizado` no sirve para eso porque se resetea en cada
   * edición. Ver docs/plan-eliminar-proveedor-producto-tela-2026-09-05.md.
   */
  creadoRemoto?: boolean
  /** URL/key real de la tarjeta ya subida — ver comentario en TelaPendiente.fotoFrenteUrl. */
  tarjetaUrl?: string
}

/** Foto local del producto con etiqueta descriptiva. */
export interface FotoProductoPendiente {
  id: string
  etiqueta: string
  blob: ArrayBuffer
  /**
   * URL real ya subida — se guarda apenas se sube con éxito, y se reutiliza
   * si el blob local se vacía a las 72h (ver limpiarBlobsSincronizadosAntiguos)
   * y hay que resincronizar por editar otra cosa (ej. el FOB). Solo hace
   * falta acá, no en color/estructura ni en telas: esas son columnas simples
   * donde alcanza con omitirlas si no hay nada nuevo — pero `fotos` es un
   * arreglo que se reemplaza entero en cada sync, así que hay que saber la
   * URL real de cada foto para no perderla. Ver
   * docs/hallazgo-blob-vacio-post-72h-2026-09-06.md.
   */
  url?: string
}

export interface ProductoPendiente {
  id?: number
  productoId: string
  proveedorCodigo: string
  mueble: string
  categoria: string
  fob?: number
  moq?: string
  unidadesPorContenedor?: string
  colorEstructura?: string
  /** Foto opcional asociada al color de estructura. */
  colorEstructuraFotoBlob?: ArrayBuffer
  notas: string
  atributosEspeciales: string[]
  fotosBlobs: FotoProductoPendiente[]
  telas: TelaPendiente[]
  creadoEn: number
  /** Usamos número porque Dexie indexa mejor este campo. */
  sincronizado: 0 | 1
  intentos: number
  ultimoError?: string
  /** Ver el mismo campo en ProveedorPendiente — misma razón, mismo comportamiento. */
  creadoRemoto?: boolean
  /** URL/key real de la foto de color/estructura ya subida — ver comentario en TelaPendiente.fotoFrenteUrl. */
  colorEstructuraFotoUrl?: string
}

/**
 * Cola de borrados pendientes de aplicar en Supabase (Storage + Postgres).
 * Separada de las tablas `proveedores`/`productos` a propósito: cuando el
 * usuario elimina algo, la fila local se borra YA (para que desaparezca de
 * la UI al instante, sin pantallas de "eliminando…") — esta tabla es lo único
 * que le queda al sincronizador para terminar el trabajo del lado remoto.
 * Se procesa en 3 pasadas (tela → producto → proveedor) para nunca intentar
 * borrar un padre antes que sus hijos. Ver
 * docs/plan-eliminar-proveedor-producto-tela-2026-09-05.md.
 */
export type TipoBorrado = 'tela' | 'producto' | 'proveedor'

/**
 * Backend real donde vive una foto — 2026-09-07, para el borrado en cascada.
 * No alcanza con "intentar borrar en los dos storages a ciegas": cada foto
 * vive en uno solo (Supabase o S3, según cuál haya tenido éxito al subirla,
 * ver SubidorConFallback), y si se intenta en el que no es, en el mejor
 * caso no pasa nada (operación idempotente) pero en el peor caso enmascara
 * un fallo real del storage correcto (ver hallazgo del 2026-09-07: borrar
 * "tenía éxito" en Supabase para una foto que solo existía en S3, dejando
 * el archivo huérfano ahí sin ningún aviso). Por eso cada ruta encolada para
 * borrar sabe explícitamente en qué backend vive.
 */
export type BackendStorage = 'supabase' | 's3'

export interface RutaDeBorrado {
  ruta: string
  backend: BackendStorage
}

export interface BorradoPendiente {
  id?: number
  tipo: TipoBorrado
  /** Requerido para tipo 'proveedor'. */
  codigoProveedor?: string
  /** Requerido para tipo 'producto' y 'tela'. */
  productoId?: string
  /** Requerido para tipo 'tela'. */
  telaId?: string
  /** Rutas de Storage a borrar, cada una con su backend real (ver BackendStorage). */
  rutasStorage: RutaDeBorrado[]
  creadoEn: number
  intentos: number
  ultimoError?: string
}

/** Convertimos una foto capturada (Blob) al formato que persistimos en IndexedDB. */
export async function blobAArrayBuffer(blob: Blob): Promise<ArrayBuffer> {
  return blob.arrayBuffer()
}

/** Reconstruimos un Blob mostrable (preview, subida) desde lo persistido. */
export function arrayBufferABlob(buffer: ArrayBuffer, tipo = 'image/jpeg'): Blob {
  return new Blob([buffer], { type: tipo })
}

async function migrarBlobAArrayBuffer(valor: unknown): Promise<ArrayBuffer | undefined> {
  if (!valor) return undefined
  // Si ya es un ArrayBuffer (o quedo vacio) lo dejamos igual; solo migramos Blobs viejos.
  if (valor instanceof Blob) return valor.arrayBuffer()
  return valor as ArrayBuffer
}

class FeriaDB extends Dexie {
  proveedores!: Table<ProveedorPendiente, number>
  productos!: Table<ProductoPendiente, number>
  borradosPendientes!: Table<BorradoPendiente, number>

  constructor() {
    super('feria-china-2026')
    this.version(2).stores({
      proveedores: '++id, sincronizado, codigo, creadoEn',
      productos: '++id, sincronizado, productoId, proveedorCodigo, creadoEn',
    })

    // v3: migramos fotos guardadas como Blob a ArrayBuffer (bug de Safari/WebKit
    // que vacia Blobs guardados en IndexedDB al releerlos, ver hallazgo en docs/).
    this.version(3)
      .stores({
        proveedores: '++id, sincronizado, codigo, creadoEn',
        productos: '++id, sincronizado, productoId, proveedorCodigo, creadoEn',
      })
      .upgrade(async (tx) => {
        await tx
          .table('proveedores')
          .toCollection()
          .modify(async (p: ProveedorPendiente) => {
            p.tarjetaBlob = await migrarBlobAArrayBuffer(p.tarjetaBlob)
          })

        await tx
          .table('productos')
          .toCollection()
          .modify(async (p: ProductoPendiente) => {
            p.colorEstructuraFotoBlob = await migrarBlobAArrayBuffer(p.colorEstructuraFotoBlob)
            p.fotosBlobs = await Promise.all(
              (p.fotosBlobs ?? []).map(async (f) => ({ ...f, blob: (await migrarBlobAArrayBuffer(f.blob))! })),
            )
            p.telas = await Promise.all(
              (p.telas ?? []).map(async (t) => ({
                ...t,
                fotoFrenteBlob: await migrarBlobAArrayBuffer(t.fotoFrenteBlob),
                fotoReversoBlob: await migrarBlobAArrayBuffer(t.fotoReversoBlob),
              })),
            )
          })
      })

    // v4: cola de borrados (feature eliminar proveedor/producto/tela,
    // 2026-09-05) — solo agrega una tabla nueva, no toca datos existentes.
    this.version(4).stores({
      proveedores: '++id, sincronizado, codigo, creadoEn',
      productos: '++id, sincronizado, productoId, proveedorCodigo, creadoEn',
      borradosPendientes: '++id, tipo, creadoEn',
    })
  }
}

export const db = new FeriaDB()

export async function contarPendientes(): Promise<number> {
  // Incluye borrados pendientes: si no, el badge "en cola" y el botón
  // "Sincronizar" de la UI mobile ignorarían por completo que hay borrados
  // esperando aplicarse contra Supabase.
  const [prov, prod, borrados] = await Promise.all([
    db.proveedores.where('sincronizado').equals(0).count(),
    db.productos.where('sincronizado').equals(0).count(),
    db.borradosPendientes.count(),
  ])
  return prov + prod + borrados
}

/** Listamos proveedores locales sin depender de red. */
export async function listarProveedoresLocal(): Promise<ProveedorPendiente[]> {
  return db.proveedores.orderBy('creadoEn').reverse().toArray()
}

/**
 * Renombra en cascada un proveedor con código TEMPORAL (ver
 * domain/codigo-proveedor.ts) al código real ya reservado — proveedor,
 * cada uno de sus productos (`productoId` empieza con el código del
 * proveedor) y cada tela dentro de esos productos (`tela.id` empieza con el
 * `productoId`). Se llama justo antes de subir nada a Supabase, para que lo
 * que se sincronice ya lleve el código definitivo — ver
 * `sincronizarProveedorReal` en sincronizador.ts.
 */
export async function renombrarProveedorLocal(
  idProveedorLocal: number,
  codigoTemporal: string,
  codigoFinal: string,
): Promise<void> {
  await db.proveedores.update(idProveedorLocal, { codigo: codigoFinal })

  const productos = await db.productos.where('proveedorCodigo').equals(codigoTemporal).toArray()
  for (const p of productos) {
    const productoIdNuevo = codigoFinal + p.productoId.slice(codigoTemporal.length)
    const telasRenombradas = p.telas.map((t) => ({ ...t, id: productoIdNuevo + t.id.slice(p.productoId.length) }))
    await db.productos.update(p.id!, {
      proveedorCodigo: codigoFinal,
      productoId: productoIdNuevo,
      telas: telasRenombradas,
    })
  }
}

export async function contarProductosDeProveedor(codigo: string): Promise<number> {
  return db.productos.where('proveedorCodigo').equals(codigo).count()
}

/** Listamos productos locales del proveedor activo. */
export async function listarProductosLocalDeProveedor(codigo: string): Promise<ProductoPendiente[]> {
  return db.productos.where('proveedorCodigo').equals(codigo).toArray()
}

/** Devolvemos errores recientes de sincronización para mostrarlos en la UI. */
export async function obtenerUltimosErroresSync(): Promise<string[]> {
  const [proveedoresConError, productosConError, borradosConError] = await Promise.all([
    db.proveedores.where('sincronizado').equals(0).and((p) => Boolean(p.ultimoError)).toArray(),
    db.productos.where('sincronizado').equals(0).and((p) => Boolean(p.ultimoError)).toArray(),
    // Un borrado que no logra completarse (Storage/Postgres) no debe quedar
    // silencioso: la fila local ya desapareció de la UI, así que este es el
    // único lugar donde la persona se entera de que sigue vivo en Supabase.
    db.borradosPendientes.toCollection().and((b) => Boolean(b.ultimoError)).toArray(),
  ])
  const mensajes = new Set<string>()
  for (const p of proveedoresConError) mensajes.add(p.ultimoError!)
  for (const p of productosConError) mensajes.add(p.ultimoError!)
  for (const b of borradosConError) mensajes.add(b.ultimoError!)
  return [...mensajes]
}

// ---------- Limpieza de fotos ya respaldadas en Supabase ----------

/**
 * Una vez que un proveedor/producto se sincronizo con exito, la copia real
 * (la que ve todo el equipo, la que se exporta a PPT) vive en Supabase
 * Storage/Postgres — el ArrayBuffer que quedo en este dispositivo es
 * redundante. Sin esto, IndexedDB crece sin limite durante toda la feria
 * (nada lo borraba antes), con riesgo real de eviccion del navegador
 * (especialmente Safari/iOS bajo presion de espacio).
 *
 * Retencion de 72h en vez de limpiar apenas se sincroniza: `iniciarEdicion`
 * (captura-producto.ts) permite reabrir un producto YA sincronizado para
 * seguir agregandole telas/fotos, y reconstruye las fotos existentes desde
 * este mismo ArrayBuffer — borrarlo de inmediato rompería esa edicion. 72h
 * da margen para corregir/completar al dia siguiente sin acumular toda la
 * semana de feria. Los componentes que muestran estas miniaturas
 * (mini-foto-blob, fila-foto, fila-preset) ya ignoran un blob vacio en vez
 * de mostrar una imagen rota si alguien reabre algo mas viejo que eso.
 */
const RETENCION_BLOBS_MS = 72 * 60 * 60 * 1000
const BUFFER_VACIO = new ArrayBuffer(0)

function estaVacio(buf?: ArrayBuffer): boolean {
  return !buf || buf.byteLength === 0
}

export async function limpiarBlobsSincronizadosAntiguos(): Promise<void> {
  const limite = Date.now() - RETENCION_BLOBS_MS

  const proveedores = await db.proveedores
    .where('sincronizado')
    .equals(1)
    .and((p) => p.creadoEn < limite && !estaVacio(p.tarjetaBlob))
    .toArray()
  for (const p of proveedores) {
    await db.proveedores.update(p.id!, { tarjetaBlob: BUFFER_VACIO })
  }

  const productos = await db.productos
    .where('sincronizado')
    .equals(1)
    .and((p) => p.creadoEn < limite)
    .toArray()
  for (const p of productos) {
    const yaLimpio =
      estaVacio(p.colorEstructuraFotoBlob) &&
      p.fotosBlobs.every((f) => estaVacio(f.blob)) &&
      p.telas.every((t) => estaVacio(t.fotoFrenteBlob) && estaVacio(t.fotoReversoBlob))
    if (yaLimpio) continue

    await db.productos.update(p.id!, {
      colorEstructuraFotoBlob: p.colorEstructuraFotoBlob ? BUFFER_VACIO : undefined,
      fotosBlobs: p.fotosBlobs.map((f) => ({ ...f, blob: BUFFER_VACIO })),
      telas: p.telas.map((t) => ({
        ...t,
        fotoFrenteBlob: t.fotoFrenteBlob ? BUFFER_VACIO : undefined,
        fotoReversoBlob: t.fotoReversoBlob ? BUFFER_VACIO : undefined,
      })),
    })
  }
}
